import { t, type MessageKey } from '../i18n/index.ts';
import type { CountryRules } from '../lib/country.ts';
import { StatusBadge } from './ui.tsx';

/** "1 January" for a financial year starting on 01-01. */
const yearStart = (mmdd: string, locale: string) => {
  const [m, d] = mmdd.split('-').map(Number);
  try {
    return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(Date.UTC(2026, (m ?? 1) - 1, d ?? 1)));
  } catch {
    return mmdd;
  }
};

const sample = (locale: string) => {
  try {
    return `${new Intl.DateTimeFormat(locale, { timeZone: 'UTC' }).format(new Date(Date.UTC(2026, 10, 30)))} · ${new Intl.NumberFormat(locale, { minimumFractionDigits: 2 }).format(1234.5)}`;
  } catch {
    return locale;
  }
};

/**
 * What the business's country pack sets up, in plain words: money, tax, formats, paper, financial
 * year, chart of accounts, the tax office connection and the notes printed on invoices.
 */
export function CountryPackFacts({ rules }: { rules: CountryRules }) {
  const fiscal = rules.fiscal.status;
  const notes = [rules.mentions.reverseCharge, rules.mentions.latePayment, rules.mentions.smallBusiness].filter((n): n is string => !!n);
  return (
    <dl className="report kv country-pack">
      <dt>{t('country.f_currency')}</dt>
      <dd>{rules.currency}</dd>
      <dt>{t('country.f_tax')}</dt>
      <dd>{t(rules.prices === 'with_tax' ? 'country.tax_included' : 'country.tax_added', { name: rules.taxName })}</dd>
      <dt>{t('country.f_formats')}</dt>
      <dd className="tabular">{sample(rules.formats.locale)}</dd>
      <dt>{t('country.f_paper')}</dt>
      <dd>{rules.paper}</dd>
      <dt>{t('country.f_year')}</dt>
      <dd>{yearStart(rules.fiscalYearStart, rules.formats.locale)}</dd>
      <dt>{t('country.f_chart')}</dt>
      <dd>{t(`country.chart_${rules.chart}` as MessageKey)}</dd>
      <dt>{t('country.f_fiscal')}</dt>
      <dd>
        <StatusBadge
          tone={fiscal === 'ready' ? 'ok' : fiscal === 'none' ? 'neutral' : 'info'}
          icon={fiscal === 'ready' ? 'check' : fiscal === 'none' ? 'minus' : 'clock'}
          label={t(`country.fiscal_${fiscal}` as MessageKey)}
        />
        {rules.einvoice.formats.length ? <span className="muted small country-pack-formats">{rules.einvoice.formats.join(' · ')}</span> : null}
        {rules.einvoice.note ? <span className="muted small country-pack-formats">{rules.einvoice.note}</span> : null}
      </dd>
      {notes.length ? (
        <>
          <dt>{t('country.f_notes')}</dt>
          <dd>
            {notes.map((n) => (
              <span key={n} className="country-pack-note">
                {n}
              </span>
            ))}
          </dd>
        </>
      ) : null}
    </dl>
  );
}
